// "Cost Realization" list (see cost_realizations.py) - records are GLOBAL
// (fund-wide, not scoped to one reporting: a realization marked once stays
// applied to every future reporting) so this table shows every realization
// regardless of which reporting created it. Delete still needs the CURRENT
// reporting_id (query param) purely for calc-service stale-marking/audit.

import React, { useEffect, useState, useCallback } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import CostRealizationDialog from './CostRealizationDialog';
import LoadingState from '../../common/LoadingState';
import EmptyState from '../../common/EmptyState';
import errorMessage from '../../../helpers/errorMessage';

const humanize = (id) => (id || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatAmount = (n) => (n === null || n === undefined ? '' : n.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('default', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const CostRealizationPanel = ({ reportingId, setLoading, onChange }) => {
  const [realizations, setRealizations] = useState(null);
  const [error, setError] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRealizations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosWithAuth.get('/compartment-report/cost-realizations');
      setRealizations(res.data.realizations || []);
      setError(null);
    } catch (err) {
      console.error('[CostRealizationPanel] fetch error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to load cost realizations.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRealizations();
  }, [fetchRealizations]);

  const handleDelete = async (id) => {
    if (!reportingId) return;
    setDeletingId(id);
    setLoading(true);
    try {
      const res = await axiosWithAuth.delete(`/compartment-report/cost-realizations/${id}`, {
        params: { reporting_id: reportingId },
      });
      setRealizations(res.data.realizations || []);
      setError(null);
      if (onChange) onChange(res.data.realizations);
    } catch (err) {
      console.error('[CostRealizationPanel] delete error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to delete cost realization.'));
    } finally {
      setDeletingId(null);
      setLoading(false);
    }
  };

  const handleSaved = (updatedRealizations) => {
    setRealizations(updatedRealizations || []);
    if (onChange) onChange(updatedRealizations);
  };

  return (
    <div>
      <div className="ex-section-header">
        <h4 className="ex-section-title">Cost Realizations</h4>
        <Button label="Define Cost Realizations" icon="pi pi-plus" onClick={() => setDialogVisible(true)} disabled={!reportingId} />
      </div>

      {error && <div className="ex-inline-error mb-3">{error}</div>}

      {realizations === null ? (
        <LoadingState label="Loading cost realizations" />
      ) : realizations.length === 0 ? (
        <EmptyState icon="pi-inbox" title="No cost realizations recorded yet." compact />
      ) : (
        <DataTable value={realizations} dataKey="id" size="small" showGridlines stripedRows paginator rows={10}>
          <Column field="platform" header="Originator" />
          <Column field="quarter" header="Quarter" />
          <Column header="Cost Item(s)" body={(r) => (r.cost_items || []).map(humanize).join(', ')} />
          <Column header="Amount" headerClassName="cr-num" bodyClassName="cr-num" body={(r) => formatAmount(r.amount)} />
          <Column field="note" header="Note" />
          <Column header="Recorded" bodyClassName="cr-nowrap-cell" body={(r) => formatDate(r.created_at)} />
          <Column
            header=""
            body={(r) => (
              <Button
                icon="pi pi-trash"
                className="p-button-danger p-button-text"
                onClick={() => handleDelete(r.id)}
                loading={deletingId === r.id}
                disabled={!reportingId}
              />
            )}
          />
        </DataTable>
      )}

      <CostRealizationDialog
        reportingId={reportingId}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default CostRealizationPanel;
